
import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Card, CardContent, Chip, Button,
  Tabs, Tab, Grid, Divider, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, LinearProgress, Alert, Rating
} from '@mui/material';
import {
  LocalShipping, CheckCircle, Cancel, HourglassTop, Inventory2,
  RateReview
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { transactionAPI, reviewAPI } from '../services/api';

const MotionCard = motion(Card);

const statusColors = {
  pending: 'warning',
  accepted: 'info',
  shipped: 'primary',
  delivered: 'success',
  completed: 'success',
  rejected: 'error',
  cancelled: 'default',
};

const statusIcons = {
  pending: <HourglassTop fontSize="small" />,
  accepted: <CheckCircle fontSize="small" />,
  shipped: <LocalShipping fontSize="small" />,
  delivered: <CheckCircle fontSize="small" />,
  rejected: <Cancel fontSize="small" />,
  cancelled: <Cancel fontSize="small" />,
};

const OrdersPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shipDialog, setShipDialog] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [reviewDialog, setReviewDialog] = useState(null);
  const [reviewData, setReviewData] = useState({ rating: 5, title: '', comment: '' });

  useEffect(() => { loadOrders(); }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const [purchasesRes, salesRes] = await Promise.all([
        transactionAPI.myPurchases(),
        transactionAPI.mySales(),
      ]);
      setPurchases(purchasesRes.data);
      setSales(salesRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (id) => {
    try {
      await transactionAPI.update(id, { status: 'accepted' });
      toast.success('Order accepted');
      loadOrders();
    } catch (err) { toast.error('Failed'); }
  };

  const handleReject = async (id) => {
    try {
      await transactionAPI.update(id, { status: 'rejected' });
      toast.success('Order rejected');
      loadOrders();
    } catch (err) { toast.error('Failed'); }
  };

  const handleShip = async () => {
    try {
      await transactionAPI.update(shipDialog, { status: 'shipped', tracking_number: trackingNumber });
      toast.success('Marked as shipped');
      setShipDialog(null);
      setTrackingNumber('');
      loadOrders();
    } catch (err) { toast.error('Failed'); }
  };

  const handleDelivered = async (id) => {
    try {
      await transactionAPI.update(id, { status: 'delivered' });
      toast.success('Confirmed delivery');
      loadOrders();
    } catch (err) { toast.error('Failed'); }
  };

  const handleCancel = async (id) => {
    try {
      await transactionAPI.cancel(id);
      toast.success('Order cancelled');
      loadOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleReview = async () => {
    try {
      await reviewAPI.create({
        transaction_id: reviewDialog,
        rating: reviewData.rating,
        title: reviewData.title,
        comment: reviewData.comment,
      });
      toast.success('Review submitted!');
      setReviewDialog(null);
      setReviewData({ rating: 5, title: '', comment: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const orders = tab === 0 ? purchases : sales;
  const isSeller = tab === 1;

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '80vh', py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" fontWeight={700} gutterBottom>My Orders</Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label={`My Purchases (${purchases.length})`} />
          <Tab label={`Orders Received (${sales.length})`} />
        </Tabs>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {orders.length === 0 && !loading && (
          <Card sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
            <Inventory2 sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No orders yet</Typography>
          </Card>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {orders.map((order, i) => (
            <MotionCard
              key={order.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              sx={{ borderRadius: 3 }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Order #{order.order_number}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      Item #{order.item_id} × {order.quantity}
                    </Typography>
                  </Box>
                  <Chip
                    icon={statusIcons[order.status]}
                    label={order.status.toUpperCase()}
                    color={statusColors[order.status]}
                    size="small"
                  />
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Offered Price</Typography>
                    <Typography fontWeight={600}>₹{order.offered_price?.toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Final Price</Typography>
                    <Typography fontWeight={600}>
                      {order.final_price ? `₹${order.final_price.toLocaleString()}` : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Payment</Typography>
                    <Typography fontWeight={600}>{order.payment_status}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Date</Typography>
                    <Typography fontWeight={600}>
                      {new Date(order.created_at).toLocaleDateString('en-IN')}
                    </Typography>
                  </Grid>
                </Grid>

                {order.tracking_number && (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }} icon={<LocalShipping />}>
                    Tracking: {order.tracking_number}
                  </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {/* Seller actions */}
                  {isSeller && order.status === 'pending' && (
                    <>
                      <Button variant="contained" color="success" size="small" onClick={() => handleAccept(order.id)}>
                        Accept
                      </Button>
                      <Button variant="outlined" color="error" size="small" onClick={() => handleReject(order.id)}>
                        Reject
                      </Button>
                    </>
                  )}
                  {isSeller && order.status === 'accepted' && (
                    <Button variant="contained" size="small" startIcon={<LocalShipping />}
                      onClick={() => setShipDialog(order.id)}>
                      Mark Shipped
                    </Button>
                  )}

                  {/* Buyer actions */}
                  {!isSeller && order.status === 'pending' && (
                    <Button variant="outlined" color="error" size="small" onClick={() => handleCancel(order.id)}>
                      Cancel Order
                    </Button>
                  )}
                  {!isSeller && order.status === 'shipped' && (
                    <Button variant="contained" color="success" size="small" onClick={() => handleDelivered(order.id)}>
                      Confirm Delivery
                    </Button>
                  )}
                  {!isSeller && order.status === 'delivered' && (
                    <Button variant="outlined" size="small" startIcon={<RateReview />}
                      onClick={() => setReviewDialog(order.id)}>
                      Leave Review
                    </Button>
                  )}
                </Box>
              </CardContent>
            </MotionCard>
          ))}
        </Box>

        {/* Ship Dialog */}
        <Dialog open={!!shipDialog} onClose={() => setShipDialog(null)}>
          <DialogTitle>Ship Order</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth label="Tracking Number (optional)" sx={{ mt: 1 }}
              value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="e.g., AWB123456789"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShipDialog(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleShip}>Confirm Shipment</Button>
          </DialogActions>
        </Dialog>

        {/* Review Dialog */}
        <Dialog open={!!reviewDialog} onClose={() => setReviewDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Rate Your Experience</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
              <Typography>Rating:</Typography>
              <Rating
                value={reviewData.rating}
                onChange={(_, v) => setReviewData({ ...reviewData, rating: v })}
                precision={0.5}
              />
            </Box>
            <TextField
              fullWidth label="Title" sx={{ mb: 2 }}
              value={reviewData.title} onChange={(e) => setReviewData({ ...reviewData, title: e.target.value })}
            />
            <TextField
              fullWidth label="Comment" multiline rows={3}
              value={reviewData.comment} onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReviewDialog(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleReview}>Submit Review</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default OrdersPage;
